import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import { Toast } from '../components/ui/Notification';
import { 
  FiSettings, FiActivity, FiShield, FiCheckCircle, 
  FiAlertCircle, FiLoader 
} from 'react-icons/fi';

export default function AdminSettings() {
  const { adminUser } = useAuth();
  const [checking, setChecking] = useState(false);
  const [health, setHealth] = useState(null);
  const [notification, setNotification] = useState(null);

  const runHealthCheck = async () => {
    setChecking(true);
    setHealth(null);
    try {
      const res = await adminApi.healthCheck(adminUser.admin_session_token);
      setHealth(res);
      if (res.success) {
        setNotification({ message: 'Gateway connection verified.', type: 'success' });
      } else {
        setNotification({ message: res.message || 'Gateway reported an error.', type: 'error' });
      }
    } catch (e) {
      setHealth({ success: false, message: 'Gateway unreachable. Verify VITE_BPAS_CLIENT_PORTAL_GATEWAY_URL.' });
      setNotification({ message: 'Critical connection failure.', type: 'error' });
    } finally {
      setChecking(false);
    }
  };

  const configs = [
    { label: 'Gateway Endpoint', status: !!import.meta.env.VITE_BPAS_CLIENT_PORTAL_GATEWAY_URL },
    { label: 'Public Script URL', status: !!import.meta.env.VITE_BPAS_PUBLIC_SCRIPT_BASE_URL },
    { label: 'Portal Brand Name', status: !!import.meta.env.VITE_BPAS_CLIENT_PORTAL_PUBLIC_NAME },
    { label: 'Admin Session', status: !!adminUser.admin_session_token },
  ];

  return (
    <div className="space-y-10">
      {notification && <Toast {...notification} onClose={() => setNotification(null)} />}
      
      <header>
        <h1 className="text-4xl font-black text-slate-900 tracking-tighter">System Configuration</h1>
        <p className="text-slate-500 font-bold text-sm mt-1 uppercase tracking-widest opacity-60">Environment Stability</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-[2.5rem] p-10 border border-slate-100 shadow-sm">
          <h3 className="text-xl font-black text-slate-900 mb-8 uppercase tracking-tight">Environment Status</h3>
          <div className="space-y-4">
            {configs.map(c => (
              <div key={c.label} className="flex items-center justify-between p-5 bg-slate-50 rounded-2xl border border-slate-100">
                <span className="text-xs font-bold text-slate-600">{c.label}</span>
                <div className="flex items-center gap-2">
                  <SafeIcon icon={c.status ? FiCheckCircle : FiAlertCircle} className={c.status ? 'text-emerald-500' : 'text-red-500'} />
                  <span className={`text-[10px] font-black uppercase tracking-widest ${c.status ? 'text-emerald-600' : 'text-red-600'}`}>
                    {c.status ? 'Configured' : 'Missing'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-navy-900 rounded-[2.5rem] p-10 text-white shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-48 h-48 bg-brand-blue/10 rounded-full blur-3xl -mr-24 -mt-24"></div>
          <div className="relative z-10">
            <h3 className="text-xl font-black mb-4 uppercase tracking-tight">Active Diagnostics</h3>
            <p className="text-slate-400 text-xs font-medium mb-8 leading-relaxed">
              Verify the connection between this frontend console and the Google Apps Script gateway.
            </p>
            
            {health && (
              <div className={`mb-8 p-6 rounded-3xl border animate-in fade-in slide-in-from-top-4 ${health.success ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`}>
                <div className="flex items-center gap-3 mb-2">
                  <SafeIcon icon={health.success ? FiCheckCircle : FiAlertCircle} className="text-xl" />
                  <span className="text-sm font-black uppercase tracking-widest">{health.success ? 'Gateway Healthy' : 'Gateway Critical'}</span>
                </div>
                <p className="text-xs font-medium opacity-80">{health.message || 'The system is operating within normal parameters.'}</p>
              </div>
            )}

            <button 
              onClick={runHealthCheck}
              disabled={checking}
              className="w-full py-5 bg-white/10 hover:bg-white/20 rounded-2xl flex items-center justify-center gap-3 text-xs font-black uppercase tracking-widest transition-all disabled:opacity-50"
            >
              {checking ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <><SafeIcon icon={FiActivity} /> Run Gateway Diagnostic</>}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-[2.5rem] p-10 border border-slate-100 shadow-sm text-center">
        <SafeIcon icon={FiShield} className="text-4xl text-slate-200 mx-auto mb-4" />
        <h4 className="text-lg font-black text-slate-900 uppercase tracking-tighter">Security Protocol</h4>
        <p className="text-slate-500 text-xs font-medium max-w-md mx-auto mt-2 leading-relaxed">
          Sensitive keys (EmailIt, SMS API, SIM IDs) are stored securely as Script Properties in Google Apps Script and are never exposed to this interface.
        </p>
      </div>
    </div>
  );
}